import { Component, forwardRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { PlaceSearchResult } from '@app/components/pr-form/pr-input-map/pr-input-map.component';
import { PrTableFormComponent } from '@app/components/pr-form/pr-table-form/pr-table-form.component';
import { PrTableColumn } from '@app/components/pr-table/pr-table.component';
import { ReferenceDao } from '@app/dao/reference/reference.dao';
import { UserDao } from '@app/dao/user/user.dao';
import * as _ from 'lodash';
import { combineLatest, of, Subject, switchMap, tap } from 'rxjs';
import { EaCompanyFormBuilder } from './ea-company-form.builder';

@Component({
  selector: 'ea-company-form',
  templateUrl: './ea-company-form.component.html',
  styleUrls: ['./ea-company-form.component.scss'],
  providers: [
    {
      provide: PrFormAbstract,
      useExisting: forwardRef(() => EaCompanyFormComponent),
    },
  ],
  standalone: false,
})
export class EaCompanyFormComponent
  extends EaCompanyFormBuilder
  implements OnInit
{
  users: any[];
  availableUsers: any[] = [];
  _ = _;
  doneSubject: Subject<void> = new Subject<void>();
  countries = [];
  companyActivitySectors = [];
  companyLegalForms = [
    { id: 'unknow', label: 'Inconnu' },

    { id: 'ei', label: 'E.I.' },
    { id: 'eirl', label: 'E.I.R.L.' },
    { id: 'micro_entreprise', label: 'Micro-entreprise' },
    { id: 'auto_entrepreneur', label: 'Auto-entrepreneur' },
    { id: 'agent_commercial', label: 'Agent-commercial' },

    { id: 'sa', label: 'S.A.' },
    { id: 'sas', label: 'S.A.S.' },
    { id: 'sasu', label: 'S.A.S.U.' },
    { id: 'sarl', label: 'S.A.R.L.' },
    { id: 'eurl', label: 'E.U.R.L.' },
    { id: 'snc', label: 'S.N.C.' },
    { id: 'scs', label: 'S.C.S.' },
    { id: 'sca', label: 'S.C.A.' },

    { id: 'sc', label: 'S.C.' },
    { id: 'sci', label: 'S.C.I.' },
    { id: 'scm', label: 'S.C.M.' },
    { id: 'scp', label: 'S.C.P.' },
    { id: 'scea', label: 'S.C.E.A.' },
    { id: 'gfa', label: 'G.F.A.' },

    { id: 'earl', label: 'E.A.R.L.' },
    { id: 'gaec', label: 'G.A.E.C.' },

    { id: 'selarl', label: 'S.E.L.A.R.L.' },
    { id: 'selas', label: 'S.E.L.A.S.' },
    { id: 'selafa', label: 'S.E.L.A.F.A.' },
    { id: 'selca', label: 'S.E.L.C.A.' },

    { id: 'scop', label: 'S.C.O.P.' },
    { id: 'scic', label: 'S.C.I.C.' },
    { id: 'sce', label: 'S.C.E.' },

    { id: 'association_1901', label: 'Association loi 1901' },
    { id: 'association_utilite_publique', label: "Association reconnue d'utilité publique" },
    { id: 'association_cultuelle', label: 'Association cultuelle' },
    { id: 'fondation', label: 'Fondation' },
    { id: 'fonds_dotation', label: 'Fonds de dotation' },

    { id: 'gie', label: 'G.I.E.' },
    { id: 'geie', label: 'G.E.I.E.' },
    { id: 'epic', label: 'E.P.I.C.' },
    { id: 'epa', label: 'E.P.A.' },
    { id: 'sem', label: 'S.E.M.' },
    { id: 'spl', label: 'S.P.L.' },
    { id: 'se', label: 'S.E.' },
  ];
  companyTaxResidences = [];
  companyTaxSystems = [];
  @ViewChild('tableForm', { read: PrTableFormComponent })
  tableForm: PrTableFormComponent;

  toValue: PlaceSearchResult = { address: '' };

  representativeOptions = [
    { value: 'CO_GERANT', label: 'Co-gérant' },
    { value: 'ASSOCIATE', label: 'Associé' },
    { value: 'DIRECTEUR_GENERAL', label: 'Directeur général' },
    { value: 'GERANT', label: 'Gérant' },
    { value: 'PRESIDENT', label: 'Président' },
  ];

  columnsDefinition = [];

  buildColumns(users) {
    return [
      new PrTableColumn('Membre de la famille', 'userId', 'select', {
        span: 5,
        value: v => v || ' ',
        selectOptions: [
          ...users.map(({ id, label }) => ({ value: id, label })),
          { value: ' ', label: 'Autre' },
        ],
        updateSelectOptions: value => {
          const selectedUserIds = this.owners.controls
            .map(c => c.value.userId)
            .filter(id => id && id !== ' ');
          return [
            ...this.availableUsers
              .filter(
                user => user.id === value || !selectedUserIds.includes(user.id),
              )
              .map(({ id, label }) => ({ value: id, label })),
            { value: ' ', label: 'Autre' },
          ];
        },
        changeCallback: (value: any, row: any, column: any) => {
          this.owners.controls[row.index].patchValue({ [column.name]: value });
        },
      }),
      new PrTableColumn('Nom', 'userLastName', 'input', {
        span: 3,
        disabled: (row: any) => row.userId && row.userId !== ' ',
        changeCallback: (value: any, row: any, column: any) =>
          this.owners.controls[row.index].patchValue({
            [column.name]: value,
          }),
      }),
      new PrTableColumn('Prénom', 'userFirstName', 'input', {
        span: 3,
        disabled: (row: any) => row.userId && row.userId !== ' ',
        changeCallback: (value: any, row: any, column: any) =>
          this.owners.controls[row.index].patchValue({
            [column.name]: value,
          }),
      }),
      new PrTableColumn('Fonction', 'representativeFunction', 'select', {
        span: 3,
        selectOptions: this.representativeOptions,
        changeCallback: (value: any, row: any, column: any) =>
          this.owners.controls[row.index].patchValue({
            [column.name]: value,
          }),
      }),
      new PrTableColumn('Parts', 'userSharePercentage', 'input', {
        suffix: '%',
        span: 2,
        changeCallback: (value: any, row: any, column: any) =>
          this.owners.controls[row.index].patchValue({
            [column.name]: value / 100,
          }),
      }),
      new PrTableColumn('', 'action', 'action', { span: 2 }),
    ];
  }

  constructor(
    protected formConstructor: PrFormConstructor,
    private userDao: UserDao,
    private referenceDao: ReferenceDao,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    combineLatest([
      this.userDao.getOwners(),
      this.referenceDao.getCountries(),
      this.referenceDao.getCompanyActivitySectors(),
      this.referenceDao.getCompanyTaxSystems(),
    ]).subscribe(
      ([users, countries, companyActivitySectors, companyTaxSystems]) => {
        this.availableUsers = users.filter(user =>
          ['MAIN', 'OTHER_USER'].includes(user.relation),
        );
        this.columnsDefinition = this.buildColumns(this.availableUsers);
        this.countries = countries;
        this.companyActivitySectors = companyActivitySectors;
        this.companyTaxSystems = companyTaxSystems;
      },
    );
  }

  get title() {
    return this.entity.id ? this.entity.name : "Ajout d'une personne morale";
  }

  get owners() {
    return <UntypedFormArray>this.form.get('beneficialOwner');
  }

  formatTableDataFunction() {
    return data =>
      _(data)
        .map((data, i) => ({
          index: i,
          id: data.id,
          companyId: data.companyId,
          userId: data.userId,
          userLastName: data.userLastName,
          userFirstName: data.userFirstName,
          userSharePercentage: this.floored(data.userSharePercentage * 100),
          representativeFunction: data.representativeFunction,
        }))
        .value();
  }

  newOptionLogic() {
    let newControl = this.formBuilder.group(
      this.createModel({
        representativeFunction: this.representativeOptions[0].value,
      }),
    );
    (<UntypedFormArray>this.form.get('beneficialOwner')).push(newControl);

    this.optionLogic();
  }

  onDeleteItem(event) {
    const { id, companyId } = event || {};

    if (!!id && !!companyId) {
      this.userDao.removeBeneficialOwner(id, companyId).subscribe({
        next: () => {
          this.notificationService.success('Bénéficiaire supprimé avec succès');
          this.optionLogic();
        },
        error: err => {
          console.error(err);
          this.notificationService.error('La suppression a échoué');
        },
      });
    } else {
      this.optionLogic();
    }
  }

  optionLogic() {
    //First element at 100%
    if (+this.owners.controls.length == 1)
      return this.owners.controls[0].setValue({
        ...this.owners.controls[0].value,
        userSharePercentage: 1,
      });

    // Form untouched = even allocation
    if (!this.owners.dirty) {
      this.owners.controls[0].setValue({
        ...this.owners.controls[0].value,
        userSharePercentage: 0,
      });

      //Ignore first control in order to set the missing allocation percent inside
      _.forEach(this.owners.controls, (c, i) =>
        +i != 0
          ? c.setValue({
              ...c.value,
              userSharePercentage: this.floored(
                1 / +this.owners.controls.length,
              ),
            })
          : null,
      );

      let allocationLeft =
        this.totalAllocatedPercent < 1
          ? this.floored(1 - this.totalAllocatedPercent)
          : this.floored(this.totalAllocatedPercent - 1);
      this.owners.controls[0].setValue({
        ...this.owners.controls[0].value,
        userSharePercentage: allocationLeft,
      });
      return;
    }

    // Allocation < 1 = set value left to the control
    if (this.totalAllocatedPercent < 1) {
      let newControl = this.owners.controls[this.owners.value.length - 1];
      return newControl.setValue({
        ...newControl.value,
        userSharePercentage: 1 - this.totalAllocatedPercent,
      });
    }
  }

  get totalAllocatedPercent() {
    return this.floored(_(this.owners.value).map('userSharePercentage').sum());
  }

  get allocationPanel() {
    if (!this.owners || !this.owners.value || this.owners.value.length == 0)
      return {
        display: 'info',
        icon: 'info',
        text: 'Renseignez los bénéficiaires effectifs',
      };
    else if (this.totalAllocatedPercent > 1)
      return {
        display: 'danger',
        icon: 'error',
        text: `${Math.round(
          this.totalAllocatedPercent * 100,
        )}% au total - Vous ne pouvez pas repartir plus de 100% des parts`,
      };
    else if (this.totalAllocatedPercent < 1)
      return {
        display: 'warning',
        icon: 'warning',
        text: `Il reste ${Math.round(
          (1 - this.totalAllocatedPercent) * 100,
        )}% à répartir`,
      };
    else if (
      _.filter(this.owners.value, owner => !owner.userSharePercentage).length
    )
      return {
        display: 'danger',
        icon: 'error',
        text: 'Vous ne pouvez pas repartir 0% pour un bénéficiaires effectifs',
      };
    else if (this.totalAllocatedPercent == 1)
      return {
        display: 'success',
        icon: 'check',
        text: 'Félicitations, la répartition est correcte',
      };
  }

  floored(value) {
    return Math.round(value * 100) / 100;
  }

  onSubmit() {
    const payload = { ...this.entity, ...this.extract() };
    const { beneficialOwner } = payload;

    this.prFormService.loading();

    this.userDao
      .updateCompany(_.omit(payload, 'beneficialOwner'))
      .pipe(
        switchMap(company => {
          if (beneficialOwner?.length) {
            return combineLatest(
              beneficialOwner.map(owner =>
                this.userDao.saveBeneficialOwner(owner, company.id),
              ),
            );
          } else {
            return of(company);
          }
        }),
        tap({
          next: result => {
            this.doneSubject.next(result);
            this.notificationService.success('La sauvegarde a réussi');
            this.prFormService.loaded();
            this.prFormService.close();
          },
          error: errors => {
            console.error(errors);
            this.prFormService.loaded();
            this.notificationService.error('La sauvegarde a échoué');
          },
        }),
      )
      .subscribe();
  }
}
